// src/App.jsx
import React, { useState, useEffect } from "react";
import Home from "./pages/Home";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, Button, Typography } from "@mui/material";
import Landing from "./pages/Landing";
import Login from "./pages/Login";

import { Helmet } from "react-helmet";
import "./fonts.css";
import "./App.css";
import Geolocation from "./pages/Geolocation";
import Info from "./pages/Info";
import PetManagementHome from "./pages/PetManagementHome";
import Account from "./pages/Account";
import QrVerification from "./pages/QrVerification";
import SupplierPage from "./pages/SupplierPage";
import Supplier from "./pages/Supplier";
import TagUpdatePage from "./pages/TagUpdatePage";
import Check_Tag from "./pages/Check_Tag";
import PTag from "./pages/PTag";
import PTagPayment from "./pages/PTagPayment";
import PTagConfirmation from "./pages/PTagConfirmation";
import PTagAir from "./pages/PTagAir";
import PTagAirPayment from "./pages/PTagAirPayment";
import PTagAirPreview from "./pages/PTagAirPreview";
import PTagPreview from "./pages/PTagPreview";
import PTagAirConfirmation from "./pages/PTagAirConfirmation";
import PetBasic from "./pages/PetBasic";
import PetDetail from "./pages/PetDetail";
import PetMedical from "./pages/PetMedical";
import PetMedication from "./pages/PetMedication";
import PetDeworming from "./pages/PetDeworming";
import PetVaccination from "./pages/PetVaccination";
import PetFaceID from "./pages/PetFaceID";
import PrivacyPolicy from "./pages/PrivacyPage";
import ShoppingCart from "./pages/ShoppingCart";
import PetLostMember from "./pages/PetLostMember";
import PetLostReport from "./pages/PetLostReport";
import PetLostConfirmation from "./pages/PetLostConfirmation";
import PetFoundReport from "./pages/PetFoundReport";
import AllLostPets from "./pages/AllLostPets";
import AllLostPetsAdmin from "./pages/AllLostPetsAdmin";
import PetSearchPage from "./pages/PetSearchPage";
import PetSearchResult from "./pages/PetSearchResult";
import PetFoundSearch from "./pages/PetFoundSearch";
import PetLostSearch from "./pages/PetLostSearch";
import AboutUs from "./pages/AboutUs";
import PetTransfer from "./pages/PetTransfer";
import Notification from "./pages/Notification";
import { NotificationProvider } from "./contexts/NotificationContext";
import tokenManager from "./services/tokenManager";
import { refreshAccessToken } from "./services/apiClient";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";

const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionExpiredDialog(true);
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, []);

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      // Check if token exists in memory
      let token = tokenManager.getAuthToken();
      
      // If not in memory, try cookies (backward compatibility)
      if (!token) {
        token = Cookies.get('token');
        if (token) {
          const userId = Cookies.get('id');
          const userRole = Cookies.get('role');
          tokenManager.setAuthToken(token);
          tokenManager.setUserId(userId);
          tokenManager.setUserRole(userRole);
        }
      }
      
      // If still no token, try to refresh from refresh token cookie
      if (!token) {
        console.log('No token found, attempting refresh...');
        const newToken = await refreshAccessToken();
        if (newToken) {
          console.log('Session restored via refresh token');
        } else {
          console.log('No valid session found');
        }
      } else {
        console.log('Session restored from memory');
      }
      
      setIsInitialized(true);
    };

    restoreSession();
  }, []);

  if (!isInitialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="app">
      <Helmet>
        <title>PTag - 關懷原於這裡，一個混合科技與藝術的寵物名牌</title>
        <meta property="og:title" content="Ptag" />
        <meta property="og:site_name" content="Ptag" />
        <meta property="og:url" content="https://www.ptag.com.hk/" />
        <meta
          property="og:image"
          content="https://www.ptag.com.hk/img/fb_display.jpg"
        />
        <meta
          property="og:description"
          content="關懷原於這裡，一個混合科技與藝術的寵物牌"
        />
        <meta property="fb:app_id" content="607616469433776" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <link
          rel="alternate"
          hreflang="vi"
          href="https://vi-vn.facebook.com/"
        />
        <meta
          name="keywords"
          content="Pet, PTag, Pet Tag, Lost, Protect, Protection, 寵物, 寵物牌, 保護, 保障, 智能寵物牌, 健康, 失寵, 失狗, 失貓, 尋狗, 尋貓, 尋寵, 寵物展, 推介產品, 寵物名牌, 寵物走失, 自訂寵物牌"
        />
        <meta
          name="description"
          content="PTag - 關懷，原於這裡。一個混合科技與藝術的寵物名牌。體積很細，功能很大。PTag，一個寵物資料庫，給你的愛寵多一個保障。"
        />
        <meta name="robots" content="noodp,noydir" />
        <meta name="author" content="Pet Boys Club, 寵物仔仔" />
      </Helmet>

      <NotificationProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />

          <Route path="/aboutus" element={<AboutUs />} />

          <Route path="/landing" element={<Landing />} />
          <Route path="/access.php" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/qr_verification" element={<QrVerification />} />
          <Route path="/supplier" element={<Supplier />} />
          <Route path="/supplier-page" element={<SupplierPage />} />
          <Route path="/check_tag" element={<Check_Tag />} />
          <Route path="/tag_update_page" element={<TagUpdatePage />} />
          <Route path="/shopping_cart" element={<ShoppingCart />} />

          <Route path="/pet_lost_member" element={<PetLostMember />} />
          <Route path="/pet_lost_report" element={<PetLostReport />} />
          <Route path="/pet_lost_confirmation" element={<PetLostConfirmation />} />
          <Route path="/pet_found_report" element={<PetFoundReport />} />
          <Route path="/pet_found_search" element={<PetFoundSearch />} />
          <Route path="/all_lost_pets" element={<AllLostPets />} />
          <Route path="/all_lost_pets_admin" element={<AllLostPetsAdmin />} />
          <Route path="/pet_search_page" element={<PetSearchPage />} />
          <Route path="/pet_search_result" element={<PetSearchResult />} />
          <Route path="/pet_lost_search" element={<PetLostSearch />} />


          {/* For managing qr code */}
          <Route path="/php/qr_info.php" element={<Landing />} />

          {/* 地理位置页面路由 */}
          <Route path="/geolocation" element={<Geolocation />} />
          <Route
            path="/php/qr_info_geolocation.php"
            element={<Geolocation />}
          />
          <Route
            path="/php/qr_info_geolocation_amap.php"
            element={<Geolocation />}
          />

          <Route
            path="/php/purchase_air.php"
            element={<PTagAir />}
          />


          {/* 宠物详情页面路由 */}
          <Route path="/pet_info" element={<Info />} />
          <Route path="/php/pet_detail_home.php" element={<Info />} />
          <Route path="/register" element={<Login initialTab="register" />} />

          {/* 宠物管理页面路由 */}
          <Route path="/pet-management" element={<PetManagementHome />} />
          <Route path="/pet-management/add" element={<PetBasic />} />
          <Route path="/pet-management/add/details" element={<PetDetail />} />

          <Route path="/pet-management/edit/:petId" element={<PetBasic />} />
          <Route
            path="/pet-management/edit/:petId/details"
            element={<PetDetail />}
          />
          <Route
            path="/pet-management/edit/:petId/medical"
            element={<PetMedical />}
          />
          <Route
            path="/pet-management/edit/:petId/medication"
            element={<PetMedication />}
          />
          <Route
            path="/pet-management/edit/:petId/deworming"
            element={<PetDeworming />}
          />
          <Route
            path="/pet-management/edit/:petId/vaccination"
            element={<PetVaccination />}
          />
          <Route
            path="/pet-management/edit/:petId/faceID"
            element={<PetFaceID />}
          />
          <Route
            path="/pet-management/edit/:petId/pet_transfer"
            element={<PetTransfer />}
          />
          <Route path="/ptag" element={<PTag />} />
          <Route path="/ptag-air" element={<PTagAir />} />
          <Route path="/ptag/payment" element={<PTagPayment />} />
          <Route path="/ptag-air/payment" element={<PTagAirPayment />} />
          <Route path="/ptag/preview" element={<PTagPreview />} />
          <Route path="/ptag-air/preview" element={<PTagAirPreview />} />
          <Route
            path="/ptag-air/confirmation"
            element={<PTagAirConfirmation />}
          />
          <Route path="/account/:id" element={<Account />} />

          <Route path="/notification" element={<Notification />} />
        </Routes>
      </BrowserRouter>

      <Dialog
        open={showSessionExpiredDialog}
        onClose={() => setShowSessionExpiredDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ pt: 0, pb: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}
          >
            {t("SessionExpiredTitle")}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 16, color: "#666" }}>
            {t("SessionExpiredMessage")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FFB60C",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: "none",
              "&:hover": { backgroundColor: "#e8a300" },
            }}
            onClick={() => {
              setShowSessionExpiredDialog(false);
              window.location.href = "/login";
            }}
          >
            {t("loginFooter")}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: "none",
            }}
            onClick={() => setShowSessionExpiredDialog(false)}
          >
            {t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      </NotificationProvider>
    </div>
  );
};

export default App;
